import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { XIcon, PencilIcon, CircleNotchIcon } from '@phosphor-icons/react';
import {
  useUpdateShareableLink,
  ShareableLink,
} from 'src/api/core/useShareableLinks';
import { useQueryClient } from '@tanstack/react-query';
import {
  MultiSelect,
  MultiSelectOption,
} from '@/components/common/MultiSelect';
import { SingleSelect } from '@/components/common/SingleSelect';
import useDivisions from '@/api/organization/useDivisions';
import useRebateAgreementYears from '@/api/rebates/useRebateAgreementYears';
import { T, useGT } from 'gt-react';

type ReportLineType = 'estimates' | 'predictions' | 'reported';

const CATEGORY_OPTION_IDS = [
  'Redistribution',
  'Conditional',
  'Non-Conditional',
] as const;

interface EditShareableLinkModalProps {
  open: boolean;
  onClose: () => void;
  link: ShareableLink;
}

export function EditShareableLinkModal({
  open,
  onClose,
  link,
}: EditShareableLinkModalProps) {
  const gt = useGT();
  const queryClient = useQueryClient();
  const updateLink = useUpdateShareableLink(link.id);
  const currentYear = new Date().getFullYear();

  const lineTypeOptions = useMemo(
    () =>
      [
        {
          id: 'reported' as const,
          label: gt('Reported', { context: 'rebates report line type filter' }),
          value: 'reported' as const,
        },
        {
          id: 'estimates' as const,
          label: gt('Estimates', {
            context: 'rebates report line type filter',
          }),
          value: 'estimates' as const,
        },
        {
          id: 'predictions' as const,
          label: gt('Predictions', {
            context: 'rebates report line type filter',
          }),
          value: 'predictions' as const,
        },
      ] satisfies {
        id: ReportLineType;
        label: string;
        value: ReportLineType;
      }[],
    [gt],
  );

  const categoryOptions: MultiSelectOption[] = useMemo(
    () =>
      CATEGORY_OPTION_IDS.map((id) => {
        const label =
          id === 'Redistribution'
            ? gt('Redistribution', {
                context: 'rebates report category filter',
              })
            : id === 'Conditional'
              ? gt('Conditional', {
                  context: 'rebates report category filter',
                })
              : gt('Non-Conditional', {
                  context: 'rebates report category filter',
                });
        return { id, label, value: id };
      }),
    [gt],
  );

  // Extract reportId from resourceType (e.g., "report:division-rebate-summary" -> "division-rebate-summary")
  const reportId = link.resourceType.startsWith('report:')
    ? link.resourceType.replace('report:', '')
    : null;

  // Form state
  const [linkName, setLinkName] = useState(link.name || '');
  const [emails, setEmails] = useState<string[]>(link.allowedEmails);
  const [emailInput, setEmailInput] = useState('');

  // Filter state
  const [selectedYear, setSelectedYear] = useState<number>(
    (link.metadata?.year as number) || currentYear,
  );
  const [selectedDivisions, setSelectedDivisions] = useState<
    MultiSelectOption[]
  >([]);
  const [selectedLineType, setSelectedLineType] = useState<ReportLineType>(
    (link.metadata?.lineType as ReportLineType) || 'reported',
  );
  const [selectedCategories, setSelectedCategories] = useState<
    MultiSelectOption[]
  >(() => {
    const conditionalOption = categoryOptions.find(
      (opt) => opt.id === 'Conditional',
    );
    return conditionalOption ? [conditionalOption] : [];
  }); // Default to Conditional only

  // Fetch divisions for filter
  const { data: divisionsData, isLoading: divisionsLoading } =
    useDivisions(true);

  // Fetch available years
  const { data: availableYearsData } = useRebateAgreementYears();

  // Prepare division options
  const divisionOptions: MultiSelectOption[] = useMemo(() => {
    if (!divisionsData?.divisions) return [];
    return divisionsData.divisions.map((division) => ({
      id: division.id,
      label: division.name,
      value: division,
      group: division.tags?.[0]?.name,
    }));
  }, [divisionsData?.divisions]);

  // Prepare year options
  const yearOptions = useMemo(() => {
    const years =
      availableYearsData && 'years' in availableYearsData
        ? availableYearsData.years
        : [];
    // Ensure current year is always available
    const allYears = [...new Set([currentYear, ...years])].sort(
      (a, b) => b - a,
    );
    return allYears.map((year) => ({
      id: year.toString(),
      label: year.toString(),
      value: year,
    }));
  }, [availableYearsData, currentYear]);

  // Determine which filters to show based on report type
  const showYearFilter = !!reportId;
  const showDivisionFilter = !!reportId;
  const showLineTypeFilter =
    reportId === 'division-rebate-summary' ||
    reportId === 'supplier-tag-summary';
  const showCategoryFilter = reportId === 'division-rebate-summary';

  // Initialize selected divisions from link metadata when divisions are loaded
  useEffect(() => {
    if (divisionOptions.length > 0 && link.metadata?.divisionIds) {
      const divisionIds = link.metadata.divisionIds as string[];
      const selected = divisionOptions.filter((opt) =>
        divisionIds.includes(opt.id),
      );
      setSelectedDivisions(selected);
    }
  }, [divisionOptions, link.metadata?.divisionIds]);

  // Initialize selected categories from link metadata
  useEffect(() => {
    if (link.metadata?.categoryFilters) {
      const categoryIds = link.metadata.categoryFilters as string[];
      const selected = categoryOptions.filter((opt) =>
        categoryIds.includes(opt.id),
      );
      setSelectedCategories(selected);
    } else {
      // Default to Conditional only if not specified
      const conditionalOption = categoryOptions.find(
        (opt) => opt.id === 'Conditional',
      );
      setSelectedCategories(conditionalOption ? [conditionalOption] : []);
    }
  }, [link.metadata?.categoryFilters, categoryOptions]);

  // Reset form when link changes
  useEffect(() => {
    setLinkName(link.name || '');
    setEmails(link.allowedEmails);
    setEmailInput('');
    setSelectedYear((link.metadata?.year as number) || currentYear);
    setSelectedLineType(
      (link.metadata?.lineType as ReportLineType) || 'reported',
    );
    // selectedDivisions and selectedCategories are handled by other useEffects
  }, [link, currentYear]);

  function handleAddEmail() {
    const trimmedEmail = emailInput.trim().toLowerCase();
    if (
      trimmedEmail &&
      isValidEmail(trimmedEmail) &&
      !emails.includes(trimmedEmail)
    ) {
      setEmails([...emails, trimmedEmail]);
      setEmailInput('');
    }
  }

  function handleRemoveEmail(emailToRemove: string) {
    setEmails(emails.filter((email) => email !== emailToRemove));
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  }

  function isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  async function handleSave() {
    // Build metadata based on selected filters
    const metadata: Record<string, unknown> = {
      year: selectedYear,
    };

    if (selectedDivisions.length > 0) {
      metadata.divisionIds = selectedDivisions.map((d) => d.id);
    }

    if (showLineTypeFilter) {
      metadata.lineType = selectedLineType;
    }

    if (showCategoryFilter && selectedCategories.length > 0) {
      // Only include if not all selected (optimization)
      if (selectedCategories.length < CATEGORY_OPTION_IDS.length) {
        metadata.categoryFilters = selectedCategories.map((c) => c.id);
      }
    }

    await updateLink.mutateAsync({
      name: linkName.trim() || null,
      allowedEmails: emails,
      metadata,
    });

    // Invalidate the token query to refresh the page
    queryClient.invalidateQueries({
      queryKey: ['shareable-link-by-token', link.token],
    });

    // Also invalidate the links list
    queryClient.invalidateQueries({
      queryKey: ['shareable-links'],
    });

    onClose();
  }

  function handleClose() {
    // Reset to original values
    setLinkName(link.name || '');
    setEmails(link.allowedEmails);
    setEmailInput('');
    setSelectedYear((link.metadata?.year as number) || currentYear);
    setSelectedLineType(
      (link.metadata?.lineType as ReportLineType) || 'reported',
    );
    // Reset categories
    if (link.metadata?.categoryFilters) {
      const categoryIds = link.metadata.categoryFilters as string[];
      setSelectedCategories(
        categoryOptions.filter((opt) => categoryIds.includes(opt.id)),
      );
    } else {
      const conditionalOption = categoryOptions.find(
        (opt) => opt.id === 'Conditional',
      );
      setSelectedCategories(conditionalOption ? [conditionalOption] : []); // Default to Conditional only
    }
    onClose();
  }

  // Get the original category IDs from metadata, defaulting to Conditional only if not specified
  const originalCategoryIds = (link.metadata?.categoryFilters as string[]) || [
    'Conditional',
  ];

  const hasChanges =
    linkName.trim() !== (link.name || '') ||
    JSON.stringify(emails.sort()) !==
      JSON.stringify([...link.allowedEmails].sort()) ||
    selectedYear !== ((link.metadata?.year as number) || currentYear) ||
    selectedLineType !==
      ((link.metadata?.lineType as ReportLineType) || 'reported') ||
    JSON.stringify(selectedDivisions.map((d) => d.id).sort()) !==
      JSON.stringify(((link.metadata?.divisionIds as string[]) || []).sort()) ||
    (showCategoryFilter &&
      JSON.stringify(selectedCategories.map((c) => c.id).sort()) !==
        JSON.stringify([...originalCategoryIds].sort()));

  const canSave = emails.length > 0 && hasChanges;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PencilIcon className="h-5 w-5" />
            <T context="edit shared link modal title">Edit Shared Link</T>
          </DialogTitle>
          <DialogDescription>
            <T context="edit shared link modal description">
              Update the name, recipients, or filters for this shared link.
            </T>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Link Name */}
          <div className="space-y-2">
            <Label htmlFor="linkName">
              {gt('Link Name (optional)', {
                context: 'edit shared link name field',
              })}
            </Label>
            <Input
              id="linkName"
              placeholder={gt('e.g., Q4 2024 Rebate Summary for Acme Corp', {
                context: 'edit shared link name placeholder',
              })}
              value={linkName}
              onChange={(e) => setLinkName(e.target.value)}
            />
          </div>

          {/* Email Section */}
          <div className="space-y-2">
            <Label htmlFor="email">
              {gt('Shared with (email addresses)', {
                context: 'edit shared link email field',
              })}
            </Label>
            <div className="flex gap-2">
              <Input
                id="email"
                type="email"
                placeholder={gt('Add email address', {
                  context: 'email address input placeholder',
                })}
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleAddEmail}
                disabled={
                  !emailInput.trim() || !isValidEmail(emailInput.trim())
                }
              >
                <T context="add email to shared link">Add</T>
              </Button>
            </div>
            {emails.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {emails.map((email) => (
                  <Badge
                    key={email}
                    variant="secondary"
                    className="flex items-center gap-1 pr-1"
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => handleRemoveEmail(email)}
                      className="ml-1 rounded-full hover:bg-muted p-0.5"
                    >
                      <XIcon className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            {emails.length === 0 && (
              <p className="text-sm text-destructive">
                <T context="edit shared link email validation">
                  At least one email address is required.
                </T>
              </p>
            )}
          </div>

          {/* Filters Section */}
          {reportId && (
            <div className="border-t pt-4">
              <Label className="text-sm font-medium mb-3 block">
                <T context="edit shared link filters section">Report Filters</T>
              </Label>
              <div className="space-y-3">
                {showYearFilter && (
                  <SingleSelect
                    label={gt('Year', {
                      context: 'edit shared link year filter label',
                    })}
                    options={yearOptions}
                    value={
                      yearOptions.find((opt) => opt.value === selectedYear) ||
                      null
                    }
                    onChange={(option) => {
                      if (option) {
                        setSelectedYear(option.value);
                      }
                    }}
                    placeholder={gt('Select year...', {
                      context: 'edit shared link year filter placeholder',
                    })}
                    searchPlaceholder={gt('Search years...', {
                      context:
                        'edit shared link year filter search placeholder',
                    })}
                  />
                )}

                {showDivisionFilter && (
                  <MultiSelect
                    label={gt('Divisions', {
                      context: 'edit shared link divisions filter label',
                    })}
                    options={divisionOptions}
                    value={selectedDivisions}
                    onChange={setSelectedDivisions}
                    placeholder={gt('All divisions (no filter)', {
                      context: 'edit shared link divisions filter placeholder',
                    })}
                    searchPlaceholder={gt('Search divisions...', {
                      context:
                        'edit shared link divisions filter search placeholder',
                    })}
                    disabled={divisionsLoading}
                  />
                )}

                {showLineTypeFilter && (
                  <SingleSelect
                    label={gt('Line Type', {
                      context: 'edit shared link line type filter label',
                    })}
                    options={lineTypeOptions}
                    value={
                      lineTypeOptions.find(
                        (opt) => opt.value === selectedLineType,
                      ) || null
                    }
                    onChange={(option) => {
                      if (option) {
                        setSelectedLineType(option.value);
                      }
                    }}
                    placeholder={gt('Select line type...', {
                      context: 'edit shared link line type filter placeholder',
                    })}
                  />
                )}

                {showCategoryFilter && (
                  <MultiSelect
                    label={gt('Categories', {
                      context: 'edit shared link categories filter label',
                    })}
                    options={categoryOptions}
                    value={selectedCategories}
                    onChange={setSelectedCategories}
                    placeholder={gt('Select categories...', {
                      context: 'edit shared link categories filter placeholder',
                    })}
                    searchPlaceholder={gt('Search categories...', {
                      context:
                        'edit shared link categories filter search placeholder',
                    })}
                  />
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            <T context="dialog button">Cancel</T>
          </Button>
          <Button
            onClick={handleSave}
            disabled={updateLink.isPending || !canSave}
          >
            {updateLink.isPending && (
              <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
            )}
            <T context="save changes button">Save Changes</T>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
